import React, { memo } from "react"
import PropTypes from "prop-types"
import Icon from "components/common/Icon"

import { useTranslation } from "react-i18next"
import dayjs from "dayjs"

import { useSelector } from "react-redux"
import { numberToCurrency } from "helpers/string"
import { Card, Flex } from "antd"
import { Text } from "ui/Typography"
import styles from "./Widget.module.css"
import { DATE_FORMAT } from "modules/datepicker/constants"

Widget.propTypes = {
  stats_name: PropTypes.string.isRequired,
  isCurrency: PropTypes.bool,
  currentDate: PropTypes.string,
  currentRange: PropTypes.string,
  dateGteq: PropTypes.string,
  dateLteq: PropTypes.string
}

Widget.defaultProps = {
  isCurrency: false
}

function Widget({ stats_name, isCurrency = false, currentDate, currentRange, dateGteq, dateLteq }) {
  const { t } = useTranslation()

  const isWeek = currentRange === "week"
  const isCustom = currentRange === "custom"
  const d = currentDate ? dayjs(currentDate, DATE_FORMAT) : null

  const isDay = currentRange === "day"

  const { metadata } = useSelector((store) => store.accounting)
  const { monthly_diff, last_month, ytd, total_by_scope } = metadata[`${stats_name}_stats`] || {}
  const monthlyDiffStr = monthly_diff ?? ""
  const monthlyDiffIsPositive = Number.parseFloat(monthlyDiffStr.replaceAll(/[^0-9.-]+/g, "")) > 0
  const showLastMonth = last_month !== null && last_month !== undefined

  const dateRangeLabel = (() => {
    const label = metadata?.date_range?.label
    if (label && isDay) {
      const [start, end] = label.split(" - ")
      return start === end ? start : label
    }
    return label
  })()
  const col1Label = (() => {
    if (dateRangeLabel) return dateRangeLabel
    if (isDay && d) return d.format("ddd, MMM D")
    if ((isWeek || isCustom) && dateGteq && dateLteq) {
      const start = dayjs(dateGteq, DATE_FORMAT)
      const end = dayjs(dateLteq, DATE_FORMAT)
      return `${start.format("MMM D")} - ${end.format("MMM D")}`
    }
    return d ? d.format("MMM") : null
  })()

  return stats_name ? (
    <Card className={styles.card}>
      <div>
        <h4 className="mb-3">{t(`accounting.labels.${stats_name}`)}</h4>
        <div className={styles.statsRow}>
          <div className={styles.statCell}>
            <Text>{col1Label}</Text>
            <div className={styles.statRight}>
              <p className={styles.statValue}>{isCurrency ? numberToCurrency(total_by_scope) : total_by_scope || "—"}</p>
              {showLastMonth && (
                <Flex gap={6} align="center" className={monthlyDiffIsPositive ? "text-success" : "text-danger"}>
                  <Icon iconName={"ArrowDown"} className={monthlyDiffIsPositive ? "rotate-180" : ""} size={10} block />
                  <Text className={monthlyDiffIsPositive ? "text-success" : "text-danger"}>
                    {monthlyDiffStr || "—"}
                  </Text>
                </Flex>
              )}
            </div>
          </div>
          {showLastMonth && (
            <div className={`${styles.statCell} ${styles.statCellMiddle}`}>
              <Text>{t("accounting.labels.last_month")}</Text>
              <div className={styles.statRight}>
                <p className={styles.statValue}>{isCurrency ? numberToCurrency(last_month) : last_month || "—"}</p>
              </div>
            </div>
          )}
          <div className={styles.statCell}>
            <Text>{t("accounting.labels.year_to_date")}</Text>
            <div className={styles.statRight}>
              <p className={styles.statValue}>{isCurrency ? numberToCurrency(ytd) : ytd || "—"}</p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  ) : null
}

export default memo(Widget)
